import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useFacility } from "../../../context/FacilityContext";
import { CalendarClock, Check, ChevronDown, ClipboardList, Clock3, Mail, MessageCircle, Phone, Plus } from "lucide-react";
import { Avatar, FilterSelect, PriorityStripe, SearchBox, UndoToast, paper } from "../../components/crm/ui";
import {
  DAY, Pagination, PRIORITIES, PriorityBadge, TASK_TYPES, TaskStatusBadge, apiCall, apiGet, currentUser, dateValue,
  fetchAllPages, fmtDateTime, fmtPhone, fmtTime, initialsFor, leadCode, timeValue, toTask, usePagination, usePeople,
} from "../../components/crm/contactsKit";
import { TaskDrawer } from "../../components/crm/TaskDrawer";
import { CompleteTaskDialog } from "../../components/crm/CompleteTaskDialog";
import { VIEW_ONLY_TITLE, ViewOnlyNote, useCrmAccess } from "../../components/crm/crmAccess";
import { PrimaryButton } from "../../components/ui";
import { InlineLoader, PageLoader } from "../../components/PageLoader";
import { NO_DATA } from "../../lib/format";

/**
 * CRM → Tasks (design: screens/crm/Tasks.tsx), on lead tasks.
 *
 * Real data and actions: every open task (GET /api/leads/tasks/all, statuses
 * pending / in_progress / overdue) and, on the Completed view, done and
 * cancelled tasks; counts; complete (PATCH /api/leads/tasks/:id/complete) with
 * undo (PUT …/tasks/:id back to "To do"); reschedule (PUT
 * /api/leads/:leadId/tasks/:id); add / edit / delete in TaskDrawer. Admins see
 * the whole clinic's tasks, everyone else their own (the backend decides).
 * "My tasks" are the ones assigned to the signed-in user.
 * Tasks follow the selected location (GET /api/leads/tasks/all?facility_id=,
 * item 160; a lead with no location counts under every one). Undo after a
 * delete restores the task (POST /api/leads/tasks/:id/restore, item 162).
 * WhatsApp tasks (item 159) and moving a task to another lead (item 161) are
 * in TaskDrawer.
 */

const VIEWS = [
  { id: "my", label: "My tasks" },
  { id: "today", label: "Due today" },
  { id: "overdue", label: "Overdue" },
  { id: "upcoming", label: "Upcoming" },
  { id: "open", label: "All open" },
  { id: "completed", label: "Completed" },
];
const OPEN = ["To do", "In progress"];

function taskIcon(type) {
  if (type === "Call") return Phone;
  if (type === "WhatsApp") return MessageCircle;
  if (type === "Email") return Mail;
  if (type === "Meeting") return CalendarClock;
  return ClipboardList;
}
const dayBounds = () => {
  const now = Date.now();
  const today = new Date(now).setHours(0, 0, 0, 0);
  return { now, today, tomorrow: today + DAY, weekEnd: today + (7 - new Date(now).getDay()) * DAY };
};
function bucketFor(due) {
  const b = dayBounds();
  if (due < b.now) return "Overdue";
  if (due < b.tomorrow) return "Today";
  if (due < b.tomorrow + DAY) return "Tomorrow";
  if (due < b.weekEnd) return "This week";
  return "Later";
}

export default function Tasks() {
  const me = currentUser().id;
  const people = usePeople();
  const fac = useFacility();
  const facilityId = fac.apiParams().facility_id || null;
  const [tasks, setTasks] = useState([]);
  const [doneTasks, setDoneTasks] = useState([]);
  const [doneTotal, setDoneTotal] = useState(null);
  // The location the open tasks / completed tasks on screen were loaded for: a
  // new location shows the loader, a reload after an edit does not.
  const [openFor, setOpenFor] = useState(undefined);
  const [doneFor, setDoneFor] = useState(undefined);
  const facRef = useRef(facilityId);
  facRef.current = facilityId;
  const [view, setView] = useState("my");
  const [type, setType] = useState("");
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("");
  const [query, setQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [finishing, setFinishing] = useState([]);
  const [toast, setToast] = useState(null);
  const [reschedule, setReschedule] = useState(null);
  const access = useCrmAccess(); // view only: add, complete, reschedule and edit are off
  const canEdit = access.canEdit;
  const [customDue, setCustomDue] = useState("");

  const loadOpen = useCallback(async () => {
    const scope = facilityId ? { facility_id: facilityId } : {};
    const lists = await Promise.all(["pending", "in_progress", "overdue"].map((status) => fetchAllPages("/api/leads/tasks/all", { status, sort_by: "due_date", sort_order: "ASC", ...scope })));
    if (facRef.current !== facilityId) return; // a late answer for another location
    setTasks(lists.flatMap((l) => l || []).map(toTask));
    const totals = await Promise.all(["completed", "cancelled"].map((status) => apiGet("/api/leads/tasks/all", { status, limit: 1, ...scope })));
    if (facRef.current !== facilityId) return;
    setDoneTotal(totals.every((t) => t?.success) ? totals.reduce((n, t) => n + Number(t.pagination?.total || 0), 0) : null);
    setOpenFor(facilityId);
  }, [facilityId]);
  const loadDone = useCallback(async () => {
    const lists = await Promise.all(["completed", "cancelled"].map((status) => fetchAllPages("/api/leads/tasks/all", { status, sort_by: "due_date", sort_order: "DESC", ...(facilityId ? { facility_id: facilityId } : {}) }, 10)));
    if (facRef.current !== facilityId) return;
    setDoneTasks(lists.flatMap((l) => l || []).map(toTask));
    setDoneFor(facilityId);
  }, [facilityId]);
  const loading = openFor !== facilityId;
  const doneLoading = view === "completed" && doneFor !== facilityId;
  useEffect(() => { loadOpen(); }, [loadOpen]);
  useEffect(() => { if (view === "completed") loadDone(); }, [view, loadDone]);
  const reload = () => { loadOpen(); if (view === "completed") loadDone(); };

  const b = dayBounds();
  const openTasks = tasks.filter((task) => OPEN.includes(task.status));
  const mine = (task) => me && String(task.assigneeId) === String(me);
  const counts = {
    my: openTasks.filter(mine).length,
    today: openTasks.filter((task) => task.due >= b.today && task.due < b.tomorrow).length,
    overdue: openTasks.filter((task) => task.due < b.now).length,
    upcoming: openTasks.filter((task) => task.due >= b.tomorrow).length,
    open: openTasks.length,
    completed: doneTotal,
  };
  const assigneeNames = useMemo(() => [...new Set([...people.map((p) => p.name), ...tasks.map((t) => t.assignee).filter(Boolean)])], [people, tasks]);

  const filteredTasks = useMemo(() => {
    const q = query.trim().toLowerCase();
    const digits = q.replace(/\D/g, "");
    const bounds = dayBounds();
    const base = view === "completed" ? doneTasks : tasks.filter((task) => OPEN.includes(task.status) || finishing.includes(task.id));
    return base.filter((task) => {
      if (view === "my" && !(me && String(task.assigneeId) === String(me))) return false;
      if (view === "today" && (task.due < bounds.today || task.due >= bounds.tomorrow)) return false;
      if (view === "overdue" && task.due >= bounds.now) return false;
      if (view === "upcoming" && task.due < bounds.tomorrow) return false;
      if (type && task.type !== type) return false;
      if (assignee && task.assignee !== assignee) return false;
      if (priority && task.priority !== priority) return false;
      if (!q) return true;
      const content = [task.title, task.leadName, leadCode(task.leadId), String(task.leadId)].join(" ").toLowerCase();
      return content.includes(q) || (digits.length >= 3 && task.leadPhone.includes(digits));
    }).sort((a, c) => (view === "completed" ? c.due - a.due : a.due - c.due));
  }, [view, doneTasks, tasks, finishing, type, assignee, priority, query, me]);

  const listRef = useRef(null);
  const pg = usePagination(filteredTasks.length, JSON.stringify({ view, type, assignee, priority, query }));
  const pageTasks = useMemo(() => filteredTasks.slice(pg.start, pg.end), [filteredTasks, pg.start, pg.end]);
  const groups = useMemo(() => {
    const labels = view === "completed" ? ["Completed"] : ["Overdue", "Today", "Tomorrow", "This week", "Later"];
    return labels.map((label) => ({ label, items: pageTasks.filter((task) => (view === "completed" ? label === "Completed" : bucketFor(task.due) === label)) })).filter((group) => group.items.length > 0);
  }, [pageTasks, view]);

  function closeDrawer() { setDrawerOpen(false); setEditing(null); }
  function openAdd() { setEditing(null); setDrawerOpen(true); }
  function openEdit(task) { setEditing(task); setDrawerOpen(true); }
  // The check opens "Complete task?" with an optional note; the note is saved
  // on the task and in the lead's history.
  const [completing, setCompleting] = useState(null);
  function completeTask(task) {
    if (finishing.includes(task.id)) return;
    setCompleting(task);
  }
  async function confirmComplete(task, notes) {
    setCompleting(null);
    if (finishing.includes(task.id)) return;
    setFinishing((current) => [...current, task.id]);
    setTasks((list) => list.map((t) => (t.id === task.id ? { ...t, status: "Done", completionNotes: notes || t.completionNotes } : t)));
    try {
      await apiCall("patch", `/api/leads/tasks/${task.id}/complete`, notes ? { completion_notes: notes } : {});
      setToast({ message: "Task completed", task });
    } catch (e) {
      setToast({ message: e.message });
    }
    window.setTimeout(() => { setFinishing((current) => current.filter((id) => id !== task.id)); loadOpen(); }, 600);
  }
  async function undoComplete(task) {
    try { await apiCall("put", `/api/leads/${task.leadId}/tasks/${task.id}`, { status: "pending" }); } catch (e) { setToast({ message: e.message }); }
    reload();
  }
  async function restoreTask(task) {
    try { await apiCall("post", `/api/leads/tasks/${task.id}/restore`, {}); } catch (e) { setToast({ message: e.message }); }
    reload();
  }
  async function updateDue(task, due) {
    setReschedule(null);
    try { await apiCall("put", `/api/leads/${task.leadId}/tasks/${task.id}`, { due_date: dateValue(due), due_time: `${timeValue(due)}:00` }); } catch (e) { setToast({ message: e.message }); }
    reload();
  }
  function quickDue(task, dayOffset, hour) {
    const d = new Date(Date.now() + dayOffset * DAY);
    d.setHours(hour, 0, 0, 0);
    updateDue(task, d.getTime());
  }

  return <main className="mx-auto flex max-w-7xl flex-col gap-6 px-8 py-9 animate-fade-in">
    <header className="flex items-end justify-between gap-6">
      <div><h1 className="text-h1 font-semibold">Tasks</h1><p className="mt-1 text-small text-muted">{loading ? <InlineLoader size={14} /> : <>{openTasks.length} open · {counts.overdue} overdue</>}</p></div>
      <PrimaryButton disabled={!canEdit} title={!canEdit ? VIEW_ONLY_TITLE : undefined} onClick={openAdd}><Plus size={17} strokeWidth={1.75} />Add task</PrimaryButton>
    </header>
    {access.viewOnly && <ViewOnlyNote />}
    {loading ? <PageLoader label="Loading tasks…" /> : <>
      <section aria-label="Task views" className="flex flex-wrap items-center gap-1 rounded-full bg-mist p-1.5">
        {VIEWS.map((item) => <button key={item.id} aria-pressed={view === item.id} onClick={() => setView(item.id)} className={`flex h-10 items-center gap-2 rounded-full px-4 text-small font-medium transition-colors duration-150 ${view === item.id ? "bg-paper text-ink shadow-soft" : "text-muted hover:text-ink"}`}>
          {item.label}<span className={`tabular-nums ${view === item.id ? "text-ink" : "text-muted"}`}>{counts[item.id] ?? NO_DATA}</span>
        </button>)}
      </section>
      <div className="relative z-20 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <FilterSelect label="Type" value={type} options={TASK_TYPES} onChange={setType} allLabel="All types" />
          <FilterSelect label="Assigned to" value={assignee} options={assigneeNames} onChange={setAssignee} allLabel="Any assignee" />
          <FilterSelect label="Priority" value={priority} options={PRIORITIES} onChange={setPriority} allLabel="Any priority" />
        </div>
        <SearchBox value={query} onChange={setQuery} placeholder="Task, lead, phone or #id" className="ml-auto w-72" />
      </div>
      {doneLoading ? <PageLoader label="Loading tasks…" className="min-h-[40vh]" /> : tasks.length === 0 && !doneTotal ? <div className={`${paper} flex min-h-72 flex-col items-center justify-center gap-2 px-6 text-center`}><span className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-mist text-muted"><ClipboardList size={22} strokeWidth={1.75} /></span><h2 className="text-h3 font-semibold">No tasks yet</h2><p className="max-w-md text-small text-muted">Tasks help your front-desk team keep every lead moving.</p><PrimaryButton className="mt-3 h-10 px-5 text-small" onClick={openAdd}><Plus size={16} strokeWidth={1.75} />Add task</PrimaryButton></div> : filteredTasks.length === 0 ? <div className={`${paper} flex min-h-64 flex-col items-center justify-center gap-2 px-6 text-center`}><h2 className="text-h3 font-semibold">{view === "overdue" ? "Nothing overdue" : "No tasks match these filters"}</h2><p className="text-small text-muted">{view === "overdue" ? "Tasks past their due time show up here." : "Try a different search or clear the filters to see more tasks."}</p></div> : <section aria-label="Task list" className="overflow-visible">
        <div ref={listRef} className={`${paper} overflow-visible`}>
          {groups.map((group) => <div key={group.label}>
            <div className="flex items-center justify-between border-b border-line bg-mist/45 px-6 py-3 first:rounded-t-lg"><h2 className="text-small font-semibold">{group.label}</h2><span className="text-caption tabular-nums text-muted">{group.items.length}</span></div>
            <ul>{group.items.map((task, index) => {
              const Icon = taskIcon(task.type);
              const isFinishing = finishing.includes(task.id);
              const late = task.due < b.now && OPEN.includes(task.status);
              return <li key={task.id} className={`relative flex min-h-[82px] items-center gap-4 border-b border-line px-6 py-4 last:border-b-0 animate-fade-in ${isFinishing ? "opacity-40 transition-opacity duration-500" : ""}`} style={{ animationDelay: `${Math.min(index, 10) * 30}ms` }}>
                <PriorityStripe priority={task.priority} done={task.status === "Done" || task.status === "Cancelled"} />
                <button type="button" aria-label={`Complete ${task.title}`} onClick={() => completeTask(task)} disabled={!canEdit || isFinishing || task.status === "Done" || task.status === "Cancelled"} title={!canEdit ? VIEW_ONLY_TITLE : undefined} className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${task.status === "Done" ? "border-success bg-success text-paper" : "border-muted text-transparent hover:border-ink"}`}>{task.status === "Done" ? <Check size={13} strokeWidth={2.5} className="animate-check-draw" /> : null}</button>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-mist text-ink"><Icon size={16} strokeWidth={1.75} /></span>
                <div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><p className={`truncate text-small font-semibold ${task.status === "Done" ? "text-muted line-through" : ""}`}>{task.title}</p><span className="text-caption text-muted">{task.type}</span>{task.status === "In progress" && <TaskStatusBadge status={task.status} />}</div>
                  <Link to={`/crm/leads/${task.leadId}`} className="mt-1 inline-block text-caption text-muted hover:text-ink hover:underline">{task.leadName || "Unknown contact"} · {leadCode(task.leadId)}{task.leadPhone ? ` · ${fmtPhone(task.leadPhone)}` : ""}</Link>
                  {task.status === "Done" && task.completionNotes && <p className="mt-1 whitespace-pre-line text-caption text-ink"><span className="font-medium text-muted">Notes: </span>{task.completionNotes}</p>}</div>
                <p className={`hidden min-w-36 text-small lg:block ${late ? "text-danger" : "text-muted"}`}><span className="block">{late ? "Overdue" : task.due < b.tomorrow && task.due >= b.today ? "Today" : task.due >= b.tomorrow && task.due < b.tomorrow + DAY ? "Tomorrow" : fmtDateTime(task.due).split(",")[0]}</span><span className="text-caption">{task.hasTime ? fmtTime(task.due) : NO_DATA}</span></p>
                <div className="hidden min-w-20 xl:block"><PriorityBadge priority={task.priority} /></div>
                <div className="hidden min-w-36 items-center gap-2 xl:flex"><Avatar name={task.assignee || "?"} initials={initialsFor(task.assignee)} size="sm" /><span className="truncate text-small">{task.assignee || "Unassigned"}</span></div>
                {canEdit && <div className="relative flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => { setReschedule((current) => (current === task.id ? null : task.id)); setCustomDue(`${dateValue(task.due)}T${timeValue(task.due)}`); }} className="flex h-9 items-center gap-1.5 rounded-full px-3 text-caption font-medium text-muted hover:bg-mist hover:text-ink"><Clock3 size={14} strokeWidth={1.75} /><span className="hidden sm:inline">Reschedule</span><ChevronDown size={13} strokeWidth={1.75} /></button>
                  <button type="button" onClick={() => openEdit(task)} aria-label={`Edit ${task.title}`} className="flex h-9 items-center rounded-full px-3 text-caption font-medium text-muted hover:bg-mist hover:text-ink">Edit</button>
                  {reschedule === task.id && <div className="absolute right-0 top-11 z-40 flex w-60 animate-nav-in flex-col rounded-md bg-paper p-2 shadow-lift ring-1 ring-line">
                    <button type="button" onClick={() => quickDue(task, 0, 18)} className="rounded-xs px-3 py-2 text-left text-small hover:bg-mist">Today, 6 PM</button><button type="button" onClick={() => quickDue(task, 1, 10)} className="rounded-xs px-3 py-2 text-left text-small hover:bg-mist">Tomorrow, 10 AM</button><button type="button" onClick={() => quickDue(task, 7, 10)} className="rounded-xs px-3 py-2 text-left text-small hover:bg-mist">Next week</button>
                    <div className="mt-1 flex flex-col gap-2 border-t border-line px-2 pt-3"><label className="text-caption font-medium text-muted" htmlFor={`custom-due-${task.id}`}>Date and time</label><input id={`custom-due-${task.id}`} type="datetime-local" value={customDue} onChange={(event) => setCustomDue(event.target.value)} className="h-10 min-w-0 rounded-sm border border-line bg-paper px-2 text-caption text-ink" /><button type="button" onClick={() => { if (customDue) updateDue(task, new Date(customDue).getTime()); }} className="h-9 rounded-full bg-ink text-caption font-semibold text-paper">Apply</button>
                    </div>
                  </div>}
                </div>}
              </li>;
            })}</ul>
          </div>)}
        </div>
        <Pagination state={pg} noun="task" scrollTo={listRef} />
      </section>}
    </>}
    {completing && <CompleteTaskDialog task={completing} onCancel={() => setCompleting(null)} onConfirm={(notes) => confirmComplete(completing, notes)} />}
    <TaskDrawer
      open={drawerOpen}
      onClose={closeDrawer}
      taskId={editing?.id}
      task={editing || undefined}
      onSaved={() => reload()}
      onDeleted={(task) => { setToast({ message: "Task deleted", deleted: task }); reload(); }}
    />
    {toast && <UndoToast key={toast.message + (toast.task?.id ?? toast.deleted?.id ?? "")} message={toast.message} onUndo={toast.task ? () => undoComplete(toast.task) : toast.deleted ? () => restoreTask(toast.deleted) : undefined} onDone={() => setToast(null)} />}
  </main>;
}
